import React from 'react';
import { Link } from 'react-router-dom';
import { HelpDeskLogoMark } from './icons';

interface BreadcrumbItem {
    label: string;
    href: string;
}

interface HelpDeskShellProps {
    children: React.ReactNode;
    breadcrumbs?: BreadcrumbItem[];
}

/**
 * Shared page chrome for every Help Center view — header, optional
 * breadcrumb bar, footer, and the skip-link — ported from the `shell()`
 * function in docs/helpdesk-seo-plan/prototype/lib/templates.js. Everything
 * renders inside `.hc-root` so helpdesk.css stays scoped.
 *
 * `onBack` is intentionally not threaded through here — Blog.tsx's `onBack`
 * prop is likewise accepted for prop-signature parity with App.tsx's route
 * wiring but not called from within Blog's own chrome (it uses a plain
 * `<a href="/">`). The prototype's header only ever links back to
 * `/helpdesk`, never to the marketing homepage, so there is no chrome here
 * that needs the callback.
 */
const HelpDeskShell: React.FC<HelpDeskShellProps> = ({ children, breadcrumbs }) => {
    return (
        <div className="hc-root">
            <a className="hc-skip-link" href="#hc-main">Skip to content</a>
            <header className="hc-site-header">
                <div className="hc-wrap hc-site-header__inner">
                    <Link className="hc-site-header__logo" to="/helpdesk">
                        <HelpDeskLogoMark />
                        <span className="hc-site-header__word">Traffic<b>Creator</b></span>
                        <span className="hc-site-header__tag">Help Center</span>
                    </Link>
                    <nav className="hc-site-header__nav" aria-label="Primary">
                        <Link to="/helpdesk">All topics</Link>
                        <Link to="/helpdesk/support/contact-trafficcreator-support">Contact support</Link>
                        <a className="hc-btn hc-btn--ghost" href="https://traffic-creator.com/dashboard">Open dashboard</a>
                    </nav>
                </div>
            </header>

            {breadcrumbs && breadcrumbs.length > 0 && (
                <nav className="hc-crumb" aria-label="Breadcrumb">
                    <div className="hc-wrap">
                        {breadcrumbs.map((item, index) => {
                            const isLast = index === breadcrumbs.length - 1;
                            return (
                                <React.Fragment key={item.href}>
                                    {isLast ? (
                                        <span aria-current="page">{item.label}</span>
                                    ) : (
                                        <>
                                            <Link to={item.href}>{item.label}</Link>
                                            <span className="hc-crumb__sep" aria-hidden="true">/</span>
                                        </>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </nav>
            )}

            <main id="hc-main">{children}</main>

            <footer className="hc-site-footer">
                <div className="hc-wrap hc-site-footer__inner">
                    <div className="hc-site-footer__brand">
                        <HelpDeskLogoMark />
                        <span>TrafficCreator Help Center</span>
                    </div>
                    <nav className="hc-site-footer__links" aria-label="Footer">
                        <Link to="/helpdesk">Home</Link>
                        <Link to="/helpdesk/billing/how-trafficcreator-balances-work">Billing</Link>
                        <Link to="/helpdesk/ga4-analytics/why-traffic-not-showing-in-ga4">GA4</Link>
                        <Link to="/helpdesk/support/contact-trafficcreator-support">Support</Link>
                    </nav>
                </div>
            </footer>
        </div>
    );
};

export default HelpDeskShell;
